import { AddButtonProps, LoadingButtonProps } from './buttons.types';
import { LoadingButton as MuiLoadingButton } from '@mui/lab';
import { Button, ButtonProps, IconButton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Icons } from 'greener-ui-library';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

export const LoadingButton = ({ loading, children, ...props }: LoadingButtonProps) => {
  return (
    <MuiLoadingButton loading={loading} variant="contained" color="primary" data-testid="loading-button" {...props}>
      {children}
    </MuiLoadingButton>
  );
};

export function SaveButton({ children, ...rest }: ButtonProps) {
  const { t } = useTranslation();
  const buttonContent = children ? children : t('components:form.save');

  return (
    <Button variant="contained" color="primary" endIcon={<Icons.SaveOutlined />} {...rest}>
      {buttonContent}
    </Button>
  );
}

export function DiscardButton({ children, ...rest }: ButtonProps) {
  const { t } = useTranslation();
  const buttonContent = children ? children : t('components:form.discard');

  return (
    <Button variant="text" startIcon={<Icons.BackspaceOutlined />} {...rest}>
      {buttonContent}
    </Button>
  );
}

export function EditButton({ children, ...rest }: ButtonProps) {
  const { t } = useTranslation();
  const buttonContent = children ? children : t('components:form.edit');

  return (
    <Button variant="outlined" startIcon={<Icons.EditOutlined />} {...rest}>
      {buttonContent}
    </Button>
  );
}

interface RemoveButtonProps extends ButtonProps {
  children?: ReactNode;
  onClick?: () => void;
}

export function RemoveButton({ children, onClick, ...rest }: RemoveButtonProps) {
  const { t } = useTranslation();
  const buttonContent = children ? children : t('components:form.remove');

  return (
    <Button color="error" variant="text" onClick={onClick} startIcon={<Icons.Delete />} {...rest}>
      {buttonContent}
    </Button>
  );
}

export function AddButton({ onClick, children, ...props }: AddButtonProps) {
  const { t } = useTranslation();

  const buttonContent = children ? children : t('components:form.add');

  return (
    <Button
      onClick={onClick}
      data-testid="add-button"
      variant="contained"
      color="primary"
      startIcon={<Icons.Plus fill="currentColor" />}
      {...props}
    >
      {buttonContent}
    </Button>
  );
}

/**
 * Button that display text or not based on the given breakpoint
 */
export const ResponsiveButton = ({
  icon: Icon,
  breakpoint = 'xs',
  children,
  ...rest
}: {
  icon: () => JSX.Element;
  breakpoint?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  children: ReactNode;
} & Partial<ButtonProps>) => {
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.up(breakpoint));

  return matches ? (
    <Button
      size="large"
      {...rest}
      startIcon={<Icon />}
      sx={{
        fontSize: 12,
      }}
    >
      {children}
    </Button>
  ) : (
    <IconButton size="large" {...rest}>
      <Icon />
    </IconButton>
  );
};
